import { ClipboardCheck, ReceiptText, Search as SearchIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "wouter";
import { dashboardApi } from "../api";
import { useT } from "../i18n";
import { useServiceText } from "../service-text";
import Amount from "../components/Amount";
import StatusBadge from "../components/StatusBadge";
import type { RequestStatus, SearchResults } from "../types";
import { formatCredits, formatDate, monthLabel } from "../utils";

/**
 * One box over requests and the ledger.
 *
 * The query lives in the URL so a result set can be sent to a colleague, and so the
 * back button returns to the search rather than to the page before it.
 *
 * Searching is debounced but the URL is only rewritten when the reader stops
 * typing — pushing a history entry per keystroke would make the back button take
 * nine presses to leave a nine-letter word.
 */
export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const initial = params.get("q") || "";

  const [term, setTerm] = useState(initial);
  const [results, setResults] = useState<SearchResults | null>(null);
  const [state, setState] = useState<"idle" | "searching" | "failed">("idle");
  const t = useT();
  const st = useServiceText();

  // Keep the box in step when the reader arrives on a link or presses back.
  useEffect(() => { setTerm(params.get("q") || ""); }, [params]);

  useEffect(() => {
    const query = term.trim();
    if (query.length < 2) { setResults(null); setState("idle"); return; }

    let live = true;
    setState("searching");
    const timer = window.setTimeout(() => {
      dashboardApi.search(query)
        .then((data) => { if (live) { setResults(data); setState("idle"); } })
        // A failed search must not render as "nothing matched" — that is a wrong
        // answer to the question rather than an absence of one.
        .catch(() => { if (live) { setResults(null); setState("failed"); } });
      setParams(new URLSearchParams({ q: query }), { replace: true });
    }, 250);

    return () => { live = false; window.clearTimeout(timer); };
  }, [term, setParams]);

  const total = results ? results.requests.length + results.entries.length : 0;
  const short = term.trim().length > 0 && term.trim().length < 2;

  return (
    <div className="page-stack search-page">
      <section className="panel search-box-panel">
        <label className="search-box">
          <SearchIcon size={19} />
          <input
            value={term}
            onChange={(event) => setTerm(event.target.value)}
            placeholder={t("search.placeholder")}
            aria-label={t("search.placeholder")}
            autoFocus
          />
        </label>
        <p className="search-scope">{t("search.scope")}</p>
      </section>

      {short && <p className="search-hint">{t("search.tooShort")}</p>}
      {state === "failed" && <p className="search-hint is-failed">{t("search.failed")}</p>}
      {state === "searching" && !results && <p className="search-hint">{t("search.searching")}</p>}

      {results && total === 0 && (
        <div className="empty-state">
          <SearchIcon size={30} />
          <h4>{t("search.nothing", { q: term.trim() })}</h4>
          <p>{t("search.nothingHint")}</p>
        </div>
      )}

      {results && results.requests.length > 0 && (
        <section className="panel">
          <div className="panel-header compact">
            <div><span className="section-eyebrow">{t("search.requests")}</span><h3>{t("search.requestsSub")}</h3></div>
            <span>{results.requests.length}{results.truncated.requests ? "+" : ""}</span>
          </div>
          {/* Say it out loud. A list cut at 25 that does not mention it reads as
              the complete answer, and the reader stops looking. */}
          {results.truncated.requests && (
            <p className="search-truncated">{t("search.truncated")}</p>
          )}
          <ul className="search-results">
            {results.requests.map((row) => (
              <li key={row.id}>
                <ClipboardCheck size={16} />
                <div>
                  <strong>{row.title}</strong>
                  <span>{st.name(row.service_id, row.service_name)} · {monthLabel(row.billing_month)} · <Amount value={row.credits} unit={t("common.credits")} /></span>
                </div>
                <div className="search-result-end">
                  <StatusBadge status={row.status as RequestStatus} />
                  {row.status === "completed" && <Link href={`/delivery/${row.id}`}>{t("requests.openDelivery")}</Link>}
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {results && results.entries.length > 0 && (
        <section className="panel">
          <div className="panel-header compact">
            <div><span className="section-eyebrow">{t("search.ledger")}</span><h3>{t("search.ledgerSub")}</h3></div>
            <span>{results.entries.length}{results.truncated.entries ? "+" : ""}</span>
          </div>
          {results.truncated.entries && (
            <p className="search-truncated">{t("search.truncated")}</p>
          )}
          <ul className="search-results">
            {results.entries.map((row) => (
              <li key={row.id}>
                <ReceiptText size={16} />
                <div>
                  <strong>{row.title}</strong>
                  <span>
                    {st.name(row.service_id, row.service_name) || t("ledger.accountCredits")} · {formatDate(row.occurred_at)} · {monthLabel(row.month)}
                    {row.note ? ` · ${row.note}` : ""}
                  </span>
                </div>
                <div className="search-result-end">
                  <strong className={row.credits < 0 ? "credit-negative" : ""}>
                    {/* One run, so the sign does not drift to the far side in Arabic. */}
                    <Amount value={row.credits} sign="plus" />
                  </strong>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
